import * as React from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import styled from 'styled-components'
import { Helmet } from 'react-helmet'
import Layout from '../../components/Layout'
import { constants, TextSettingsProps } from '../../constants'
import TextBlock from '../../components/common/TextBlock'
import { Ingredient } from '../../data/Ingredient'
import IngredientItem from '../../components/ingredients/IngredientItem'
import IngredientList from '../../components/ingredients/IngredientList'
import Header from '../../components/Header'
import SPKeyText from '../../components/common/SPKeyText'
import IngredientsKeyVisual from '../../components/ingredients/IngredientsKeyVisual'
import MainTitle from '../../components/common/MainTitle'
import SubSection from '../../components/common/SubSection'
import Section from '../../components/common/Section'
import { renderWithNewlines } from '../../render-utils'
import TitleImage from '../../components/common/TitleImage'
import Favicon from '../../assets/favicon.ico'
import { Language } from '../../Language'
import { FontsContext } from '../../context'

const query = graphql`
  query {
    kv1: file(relativePath: { eq: "ingredients_kv/1.png" }) {
      childImageSharp {
        fluid(maxWidth: 150) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    kv2: file(relativePath: { eq: "ingredients_kv/2.png" }) {
      childImageSharp {
        fluid(maxWidth: 150) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    kv3: file(relativePath: { eq: "ingredients_kv/3.png" }) {
      childImageSharp {
        fluid(maxWidth: 150) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    kv4: file(relativePath: { eq: "ingredients_kv/4.png" }) {
      childImageSharp {
        fluid(maxWidth: 150) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    ingredients: allFile(filter: { relativeDirectory: { eq: "profiles" } }) {
      nodes {
        childImageSharp {
          fluid(maxWidth: 220) {
            ...GatsbyImageSharpFluid
            originalName
          }
        }
      }
    }
    logo: file(relativePath: { eq: "shiseido_red.png" }) {
      childImageSharp {
        fluid(maxWidth: 185) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
  }
`

interface Props {
  ingredients: Ingredient[]
  messages: any
  titles: {
    KVTitle: any
    IngredientsPolicy?: any
    OftenAskedIngredients?: any
  }
  language: Language
}

const IngredientsIndex = ({
  ingredients,
  messages,
  titles,
  language,
}: Props) => {
  const data = useStaticQuery(query)
  const fonts = React.useContext(FontsContext)

  const images = data.ingredients.nodes.map(
    (node: any) => node.childImageSharp.fluid
  ) as any[]

  const renderIngredient = (ingredient: Ingredient) => {
    const filteredImages = images.filter(
      image => image.originalName.split('.')[0] === ingredient.no
    )
    const image = filteredImages.length ? filteredImages[0] : undefined

    return (
      <IngredientItem
        key={ingredient.name}
        ingredient={ingredient}
        fluidImage={image}
        language={language}
        messages={messages}
      />
    )
  }

  return (
    <>
      <Helmet>
        <meta charSet="utf-8"></meta>
        <title>{messages.ingredients.meta.title}</title>
        <meta
          name="description"
          content={messages.ingredients.meta.description}
        ></meta>
        <meta
          name="keywords"
          content={messages.ingredients.meta.keywords}
        ></meta>
        <meta
          property="og:image"
          content="http://www.shiseidogroup.com/cmn_new/img/fb_icon_img.jpg"
        ></meta>
        <link
          rel="shortcut icon"
          href={Favicon}
          type="image/vnd.microsoft.ico"
        ></link>
      </Helmet>
      <Layout
        messages={messages}
        language={language}
        keyVisual={() => (
          <IngredientsKeyVisual
            fluidImage1={data.kv1.childImageSharp.fluid}
            fluidImage2={data.kv2.childImageSharp.fluid}
            fluidImage3={data.kv3.childImageSharp.fluid}
            fluidImage4={data.kv4.childImageSharp.fluid}
            header={() => (
              <Header
                fluidImage={data.logo.childImageSharp.fluid}
                white
                language={language}
                messages={messages}
              />
            )}
            contents={() => (
              <>
                <Title>
                  <IngredientsMainTitleImage
                    pcWidth={language === Language.JP ? '571px' : '411px'}
                    spImageWidth={language === Language.JP ? '242px' : '162px'}
                    spImageOffset={language === Language.JP ? '-40px' : '0'}
                    src={titles.KVTitle}
                    alt={messages.ingredients.keyVisual.title}
                  />
                </Title>
                <Text settings={fonts.texts.ingredientsKvText}>
                  {messages.ingredients.keyVisual.text}
                </Text>
              </>
            )}
          />
        )}
      >
        <SPKeyText>{messages.ingredients.keyVisual.text}</SPKeyText>
        <Section>
          <SubSection as="div">
            <MainTitle settings={fonts.texts.title2}>
              {titles.IngredientsPolicy ? (
                <TitleImage
                  spWidth="193px"
                  pcWidth="276px"
                  src={titles.IngredientsPolicy}
                  alt={messages.ingredients.title}
                />
              ) : (
                messages.ingredients.title
              )}
            </MainTitle>
            <TextBlock>
              {renderWithNewlines(messages.ingredients.text)}
            </TextBlock>
          </SubSection>
          <SubSection>
            <MainTitle settings={fonts.texts.title2}>
              {titles.OftenAskedIngredients ? (
                <TitleImage
                  spWidth="269px"
                  pcWidth="384px"
                  src={titles.OftenAskedIngredients}
                  alt={messages.ingredients.listTitle}
                />
              ) : (
                messages.ingredients.listTitle
              )}
            </MainTitle>
            <IngredientList>{ingredients.map(renderIngredient)}</IngredientList>
          </SubSection>
        </Section>
      </Layout>
    </>
  )
}

const Title = styled.div`
  font-size: ${constants.font.size.sp.headings.medium};
  color: ${constants.colors.text.darker};
  text-align: center;
  display: flex;
  justify-content: center;

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    margin-bottom: 64px;
    font-size: 70px;
    text-align: left;
  }
`

const Text = styled.p<TextSettingsProps>`
  display: none;

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    display: block;
    margin: 0;
    font-size: ${({ settings }) => settings.pc.size};
    color: #6d7278;
    line-height: ${({ settings }) => settings.pc.lineHeight || 1.4};
  }
`
interface IngredientsMainTitleImageProps {
  src: string
  alt: string
  pcWidth: string
  spImageWidth: string
  spImageOffset: string
}

const IngredientsMainTitleImage = ({
  src,
  alt,
  pcWidth,
  spImageWidth,
  spImageOffset,
}: IngredientsMainTitleImageProps) => {
  return (
    <IngredientsMainTitleImageWrapper
      pcWidth={pcWidth}
      spImageWidth={spImageWidth}
      spImageOffset={spImageOffset}
    >
      <img src={src} alt={alt} />
    </IngredientsMainTitleImageWrapper>
  )
}

const IngredientsMainTitleImageWrapper = styled.div<{
  pcWidth: string
  spImageWidth: string
  spImageOffset: string
}>`
  width: 162px;

  img {
    ${({ spImageWidth }) => `width: ${spImageWidth}`};
    ${({ spImageOffset }) => `margin-left: ${spImageOffset}`};
    height: auto;
  }

  @media only screen and (min-width: ${constants.breakpoints.pc}) {
    ${({ pcWidth }) => `width: ${pcWidth}`};

    img {
      width: 100%;
      margin-left: 0;
    }
  }
`

export default IngredientsIndex
